import type { ApiError, CatalogResponse, ObservationsResponse } from "@/types";

const API_BASE = "/api";

/** Narrows an unknown thrown value to the `ApiError` shape produced by `fetchJson`.
 *  Guards against e.g. a plain `TypeError` ("Failed to fetch") thrown by `fetch()`
 *  itself before a response body was ever available to shape into an ApiError. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "string"
  );
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      error: `Request failed with status ${response.status}`,
    }));
    throw error;
  }

  return response.json();
}

export async function getCatalog(signal?: AbortSignal): Promise<CatalogResponse> {
  return fetchJson<CatalogResponse>(`${API_BASE}/catalog`, signal);
}

export async function getObservations(
  seriesId: string,
  from: string,
  to: string,
  signal?: AbortSignal
): Promise<ObservationsResponse> {
  const params = new URLSearchParams({ from, to });
  return fetchJson<ObservationsResponse>(
    `${API_BASE}/observations/${encodeURIComponent(seriesId)}?${params.toString()}`,
    signal
  );
}
